import { useEffect, useRef, useState } from "react";
import { Search, Upload, Wrench, Edit2, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { api } from "../../lib/api";
import { FAMIGLIE } from "../../lib/kb";

export default function AdminListino() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [q, setQ] = useState("");
  const [famiglia, setFamiglia] = useState("");
  const [missing, setMissing] = useState(""); // R11/C4: "dn_out" | "spessore" | "dn" | ""
  const [loading, setLoading] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importing, setImporting] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState("");
  const [editing, setEditing] = useState({});
  const fileRef = useRef(null);

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const r = await api.listino({ page, q, famiglia, missing });
      setData(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [famiglia, missing]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  // R11/C4 — Run a backfill endpoint and surface result
  const runBackfill = async (which) => {
    setBackfillRunning(which);
    try {
      let res;
      if (which === "dn_out") res = await api.listinoBackfillDnOut(false);
      else if (which === "spessore") res = await api.listinoBackfillSpessore(false);
      else if (which === "pollici") res = await api.listinoReclassifyPollici(false);
      toast.success(
        `Backfill ${which}: aggiornati ${res.updated} articoli` +
          (res.already_populated
            ? ` (${res.already_populated} già popolati)`
            : "")
      );
      await load(data.page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore backfill");
    } finally {
      setBackfillRunning("");
    }
  };

  // R11/C4 — Inline edit DN_out / spessore_mm
  const startEdit = (codice, field, current) => {
    setEditing({ codice, field, value: current ?? "" });
  };
  const cancelEdit = () => setEditing({});
  const saveEdit = async () => {
    if (!editing.codice) return;
    const val = editing.value;
    const payload = {};
    payload[editing.field] =
      editing.field === "spessore_mm"
        ? val === "" ? null : parseFloat(String(val).replace(",", "."))
        : val || null;
    try {
      await api.listinoInlineEdit(editing.codice, payload);
      toast.success(`Aggiornato ${editing.field}`);
      setEditing({});
      await load(data.page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const onImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportMsg("");
    try {
      const r = await api.importListino(file);
      setImportMsg(`Importati ${r.imported} articoli e indicizzazione aggiornata.`);
      load(1);
    } catch (e) {
      setImportMsg(
        e?.response?.data?.detail || "Errore durante l'import"
      );
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-listino">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Listino articoli
          </h1>
          <p className="text-sm text-slate-500">
            Totale: <span className="font-mono">{data.total}</span> articoli
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileRef}
            onChange={(e) => onImport(e.target.files?.[0])}
            className="hidden"
            data-testid="import-input"
          />
          <Button
            type="button"
            disabled={importing}
            onClick={() => fileRef.current?.click()}
            className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm gap-2"
            data-testid="import-btn"
          >
            <Upload className="h-4 w-4" />
            {importing ? "Importazione..." : "Importa CSV"}
          </Button>
        </div>
      </header>

      {importMsg && (
        <div
          className="rounded-sm border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700"
          data-testid="import-msg"
        >
          {importMsg}
        </div>
      )}

      {/* R11/C4 — Enrich toolbar */}
      <div
        className="rounded-sm border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2"
        data-testid="r11-enrich-toolbar"
      >
        <Wrench className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-2">
          Backfill schema R11
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!!backfillRunning}
          onClick={() => runBackfill("dn_out")}
          className="rounded-sm border-slate-300 text-xs"
          data-testid="backfill-dn-out-btn"
        >
          {backfillRunning === "dn_out" ? "..." : "DN_out"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!!backfillRunning}
          onClick={() => runBackfill("spessore")}
          className="rounded-sm border-slate-300 text-xs"
          data-testid="backfill-spessore-btn"
        >
          {backfillRunning === "spessore" ? "..." : "Spessore mm"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!!backfillRunning}
          onClick={() => runBackfill("pollici")}
          className="rounded-sm border-slate-300 text-xs"
          data-testid="backfill-pollici-btn"
        >
          {backfillRunning === "pollici" ? "..." : "Pollici→DN"}
        </Button>

        <span className="ml-auto text-[11px] uppercase tracking-wider text-slate-500 mr-2">
          Filtra mancanti
        </span>
        {[
          { k: "", label: "Tutti" },
          { k: "dn_out", label: "DN_out mancante" },
          { k: "spessore", label: "Spessore mancante" },
          { k: "dn", label: "DN mancante" },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setMissing(p.k)}
            data-testid={`missing-filter-${p.k || "all"}`}
            className={`text-xs px-2.5 py-1 rounded-sm transition-colors ${
              missing === p.k
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Cerca per codice, alias, descrizione..."
            className="pl-9 rounded-sm border-slate-200 font-mono text-sm"
            data-testid="listino-search"
          />
        </div>
        <Select
          value={famiglia || "ALL"}
          onValueChange={(v) => setFamiglia(v === "ALL" ? "" : v)}
        >
          <SelectTrigger
            className="w-56 rounded-sm border-slate-200"
            data-testid="listino-filter-family"
          >
            <SelectValue placeholder="Tutte le famiglie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutte le famiglie</SelectItem>
            {Object.entries(FAMIGLIE).map(([code, label]) => (
              <SelectItem key={code} value={code}>
                <span className="font-mono text-xs text-slate-500 mr-2">
                  {code}
                </span>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => load(1)}
          className="rounded-sm border-slate-300"
          data-testid="listino-search-btn"
        >
          Cerca
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-44 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Codice
              </TableHead>
              <TableHead className="w-32 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Alias
              </TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Descrizione
              </TableHead>
              <TableHead className="w-20 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Fam.
              </TableHead>
              <TableHead className="w-24 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                DN_out
              </TableHead>
              <TableHead className="w-24 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Spessore
              </TableHead>
              <TableHead className="w-16 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                UM
              </TableHead>
              <TableHead className="w-28 text-right font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Prezzo
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                  Caricamento...
                </TableCell>
              </TableRow>
            ) : data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                  Nessun articolo trovato.
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((it) => {
                const dnOut = it.parsed?.DN_out;
                const sp = it.parsed?.spessore_mm;
                const isEditingDnOut =
                  editing.codice === it.codice_principale &&
                  editing.field === "DN_out";
                const isEditingSp =
                  editing.codice === it.codice_principale &&
                  editing.field === "spessore_mm";
                return (
                  <TableRow
                    key={it.codice_principale}
                    data-testid={`art-${it.codice_principale}`}
                  >
                    <TableCell className="font-mono text-xs text-slate-900">
                      {it.codice_principale}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {it.alias}
                    </TableCell>
                    <TableCell className="text-sm">{it.descrizione}</TableCell>
                    <TableCell>
                      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 font-mono text-[10px] rounded-sm">
                        {it.parsed?.famiglia || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {isEditingDnOut ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editing.value}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                            placeholder="DN25"
                            className="h-6 w-16 px-1 text-xs"
                            autoFocus
                          />
                          <button onClick={saveEdit} className="text-emerald-600">
                            <Check className="h-3 w-3" />
                          </button>
                          <button onClick={cancelEdit} className="text-slate-400">
                            <XIcon className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() =>
                            startEdit(it.codice_principale, "DN_out", dnOut)
                          }
                          className={`group inline-flex items-center gap-1 ${
                            !dnOut ? "text-amber-700" : "text-slate-700"
                          }`}
                          data-testid={`dn-out-edit-${it.codice_principale}`}
                        >
                          {dnOut || "—"}
                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {isEditingSp ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editing.value}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                            placeholder="1.5"
                            className="h-6 w-16 px-1 text-xs"
                            autoFocus
                          />
                          <button onClick={saveEdit} className="text-emerald-600">
                            <Check className="h-3 w-3" />
                          </button>
                          <button onClick={cancelEdit} className="text-slate-400">
                            <XIcon className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() =>
                            startEdit(it.codice_principale, "spessore_mm", sp)
                          }
                          className={`group inline-flex items-center gap-1 ${
                            sp == null ? "text-amber-700" : "text-slate-700"
                          }`}
                          data-testid={`spessore-edit-${it.codice_principale}`}
                        >
                          {sp != null ? `${sp} mm` : "—"}
                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{it.um}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      € {Number(it.prezzo).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          Pagina {data.page} di {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={data.page <= 1}
            onClick={() => load(data.page - 1)}
            className="rounded-sm border-slate-300"
            data-testid="listino-prev"
          >
            Precedente
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={data.page >= totalPages}
            onClick={() => load(data.page + 1)}
            className="rounded-sm border-slate-300"
            data-testid="listino-next"
          >
            Successiva
          </Button>
        </div>
      </div>
    </div>
  );
}
